import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { ClientMsg } from "@shared/protocol";
import type { GameStateView } from "@shared/types";
import { LogoMark } from "../components/Brand";
import { Icon } from "../components/Icon";
import { LanguagePicker } from "../components/Pickers";
import { QuestionImage } from "../components/QuestionImage";
import { RoomFrame } from "../components/RoomFrame";
import { Standings } from "../components/Standings";
import { Button, Input, Placeholder, RoomCode, Spinner, useDialog } from "../components/ui";
import { avatarUrl, randomSeed, randomStyle } from "../lib/avatar";
import { useFx } from "../lib/fx";
import { useT } from "../lib/i18n";
import { useRoom } from "../lib/room";

// ─────────────────────────────────────────────────────────────────────────────
//  The phone. One job at a time: pick a name, then hit a very large button.
//  Everything else on screen exists to explain why you can or cannot buzz.
// ─────────────────────────────────────────────────────────────────────────────

function vibrate(ms: number) {
  try {
    navigator.vibrate?.(ms);
  } catch {
    /* unsupported — a buzz without haptics still works */
  }
}

export default function Play() {
  const { code = "" } = useParams<{ code: string }>();
  const conn = useRoom(code, "player");
  const toast = useFx();
  const t = useT();

  return (
    <RoomFrame state={conn.state} status={conn.status} fatal={conn.fatal}>
      {conn.state && (
        <div className="min-h-full flex flex-col">
          <header className="flex items-center gap-2.5 px-4 py-3 border-b border-line shrink-0">
            <LogoMark size={22} />
            <span className="font-semibold truncate text-ink/85">
              {conn.state.settings.title || t("appName")}
            </span>
            <div className="flex-1" />
            <RoomCode code={code} size="sm" className="text-ink/35" />
            <LanguagePicker compact />
          </header>

          {conn.joinState === "checking" ? (
            <Placeholder>
              <span className="inline-flex items-center gap-2">
                <Spinner /> {t("connecting")}
              </span>
            </Placeholder>
          ) : conn.joinState === "out" ? (
            <JoinForm send={conn.send} />
          ) : (
            <PlayerBody
              game={conn.state}
              myId={conn.playerId}
              send={conn.send}
              onLeave={conn.leave}
            />
          )}

          {toast && (
            <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-successStrong text-white font-semibold shadow-pop anim-pop">
              <Icon name="party" />
              {toast.name} +{toast.points}
            </div>
          )}
        </div>
      )}
    </RoomFrame>
  );
}

// ───────────────────────────── join ─────────────────────────────

function JoinForm({ send }: { send: (msg: ClientMsg) => void }) {
  const t = useT();
  const [name, setName] = useState("");
  const [style, setStyle] = useState(randomStyle);
  const [seed, setSeed] = useState(randomSeed);
  const url = avatarUrl(style, seed);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        send({ t: "player:join", name: name.trim(), avatarUrl: url });
      }}
      className="flex-1 flex flex-col items-center justify-center gap-7 p-6"
    >
      <h2 className="type-display text-3xl text-accentLight">{t("joinAs")}</h2>

      <button
        type="button"
        onClick={() => {
          setStyle(randomStyle());
          setSeed(randomSeed());
        }}
        aria-label={t("shuffleAvatar")}
        className="relative group"
      >
        <img
          src={url}
          alt=""
          className="w-32 h-32 rounded-surface bg-white shadow-pop transition-transform duration-150 ease-pop group-hover:scale-[1.03] group-active:scale-95"
        />
        <span className="absolute -bottom-2 -right-2 bg-accent text-onAccent rounded-full w-10 h-10 flex items-center justify-center shadow-pop">
          <Icon name="shuffle" size="1.15rem" />
        </span>
      </button>

      <div className="w-full max-w-xs flex flex-col gap-3">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 24))}
          placeholder={t("yourName")}
          aria-label={t("yourName")}
          autoFocus
          maxLength={24}
          className="text-xl text-center py-3"
        />
        <Button type="submit" variant="primary" size="xl" block disabled={!name.trim()}>
          {t("letsGo")}
        </Button>
      </div>
    </form>
  );
}

// ───────────────────────────── in-game ─────────────────────────────

function PlayerBody({
  game,
  myId,
  send,
  onLeave,
}: {
  game: GameStateView;
  myId: string | null;
  send: (msg: ClientMsg) => void;
  onLeave: () => void;
}) {
  const t = useT();
  const dialog = useDialog();
  const me = game.players.find((p) => p.id === myId);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {me && (
        <div className="flex items-center gap-3 px-4 py-3 shrink-0 border-b border-ink/[0.07]">
          <img src={me.avatarUrl} alt="" className="w-10 h-10 rounded-control bg-white" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold truncate leading-tight">{me.name}</div>
            <div className="text-ink/35 text-xs">{t("yourScore")}</div>
          </div>
          <div
            className={`text-2xl font-bold tabular px-3 py-1 rounded-control ${
              me.score < 0 ? "text-danger bg-danger/10" : "text-accentLight bg-accent/10"
            }`}
          >
            {me.score}
          </div>
        </div>
      )}

      {game.phase === "question" ? (
        <BuzzerScreen game={game} myId={myId} send={send} />
      ) : game.phase === "wager" ? (
        <WagerScreen game={game} myId={myId} send={send} />
      ) : game.phase === "intermission" || game.phase === "final" ? (
        <Standings game={game} />
      ) : (
        <Placeholder icon="grid">{t("waitingForQuestion")}</Placeholder>
      )}

      <div className="shrink-0 p-3 flex justify-center">
        <Button
          size="sm"
          variant="ghost"
          icon="logout"
          className="text-ink/30"
          onClick={async () => {
            const ok = await dialog.confirm({
              title: t("leaveRoom"),
              body: t("leaveRoomConfirm"),
              confirmLabel: t("leaveRoom"),
              cancelLabel: t("cancel"),
              destructive: true,
            });
            if (ok) onLeave();
          }}
        >
          {t("leaveRoom")}
        </Button>
      </div>
    </div>
  );
}

function BuzzerScreen({
  game,
  myId,
  send,
}: {
  game: GameStateView;
  myId: string | null;
  send: (msg: ClientMsg) => void;
}) {
  const t = useT();
  const q = game.current;
  const first = game.buzzes[0];
  const iAmFirst = first?.playerId === myId;
  const lockedOut = myId !== null && game.lockedOut.includes(myId);
  const canBuzz = game.buzzersOpen && !lockedOut && !game.buzzes.some((b) => b.playerId === myId);

  // Confirm the press locally the moment it happens. The server is still the
  // judge, but a buzzer that feels laggy is a buzzer nobody trusts.
  const [pressed, setPressed] = useState(false);
  const pressedRef = useRef(false);
  useEffect(() => {
    if (!game.buzzersOpen) {
      setPressed(false);
      pressedRef.current = false;
    }
  }, [game.buzzersOpen, q?.points]);

  const buzz = () => {
    if (!canBuzz || pressedRef.current) return;
    pressedRef.current = true;
    setPressed(true);
    vibrate(60);
    send({ t: "player:buzz" });
  };

  const status = iAmFirst
    ? t("youAreUp")
    : lockedOut
      ? t("lockedOutYou")
      : first
        ? t("someoneElseBuzzed", { name: first.name })
        : game.buzzersOpen
          ? null
          : t("waitForBuzzers");

  return (
    <div className="flex-1 flex flex-col min-h-0 px-4 pb-2 gap-4">
      {q?.question && (
        <div className="bg-board rounded-card px-4 py-4 text-center shrink-0 max-h-[32vh] overflow-y-auto flex flex-col items-center gap-3">
          <div className="type-eyebrow text-accentLight/80">
            {q.categoryName} · {q.points}
          </div>
          <QuestionImage src={q.image} className="max-h-[16vh] w-auto rounded-control object-contain" />
          <p className="font-semibold text-lg leading-snug">{q.question}</p>
          {q.answerRevealed && q.answer && (
            <p className="text-accentLight font-semibold border-t border-ink/15 pt-3 w-full">
              {q.answer}
            </p>
          )}
        </div>
      )}

      <div className="flex-1 flex flex-col items-center justify-center gap-5 min-h-0">
        <button
          onClick={buzz}
          disabled={!canBuzz}
          className={`relative w-56 h-56 max-w-[68vw] max-h-[68vw] rounded-full type-display text-4xl transition-all duration-100 ease-pop active:scale-95 disabled:active:scale-100 ${
            iAmFirst
              ? "bg-successStrong text-white shadow-pop"
              : canBuzz
                ? "bg-dangerStrong text-white shadow-pop hover:bg-danger"
                : "bg-ink/[0.07] text-ink/25 border border-line"
          }`}
        >
          {/* A soft halo instead of a hard ring — a translucent ring over the
              themed background rendered as a muddy brown smear. */}
          {canBuzz && !iAmFirst && (
            <span
              aria-hidden
              className="absolute inset-0 rounded-full bg-dangerStrong/40 blur-2xl -z-10"
            />
          )}
          {iAmFirst ? t("youAreUp") : pressed ? t("buzzed") : t("buzz")}
        </button>

        <p aria-live="polite" className="text-center text-ink/50 min-h-[24px] px-4">
          {status}
        </p>
      </div>
    </div>
  );
}

function WagerScreen({
  game,
  myId,
  send,
}: {
  game: GameStateView;
  myId: string | null;
  send: (msg: ClientMsg) => void;
}) {
  const t = useT();
  const w = game.wager;
  const me = game.players.find((p) => p.id === myId);
  const mine = w?.entries.find((e) => e.playerId === myId);

  const max = Math.max(0, me?.score ?? 0);
  const [amount, setAmount] = useState(0);
  const [answer, setAnswer] = useState("");

  if (!w) return null;

  if (w.stage === "betting") {
    const value = Math.min(amount, max);
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-6 p-6">
        <div className="text-center">
          <h2 className="type-display text-2xl text-accentLight">{t("placeYourWagers")}</h2>
          {mine?.wagered && (
            <p className="text-success text-sm mt-2 inline-flex items-center gap-1.5">
              <Icon name="check" /> {t("wagerPlaced")}
            </p>
          )}
        </div>

        <div className="w-full max-w-xs flex flex-col gap-4">
          <div className="text-center">
            <div className="type-eyebrow text-ink/40">{t("yourWager")}</div>
            <div className="type-display text-5xl text-accentLight tabular mt-1">{value}</div>
            <div className="text-ink/35 text-xs mt-1">{t("maxWager", { n: max })}</div>
          </div>
          <input
            type="range"
            min={0}
            max={max}
            step={Math.max(1, Math.round(max / 100) || 1)}
            value={value}
            onChange={(e) => setAmount(Number(e.target.value))}
            aria-label={t("yourWager")}
            className="w-full accent-current text-accentLight"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setAmount(0)} className="flex-1">
              0
            </Button>
            <Button size="sm" onClick={() => setAmount(Math.floor(max / 2))} className="flex-1">
              ½
            </Button>
            <Button size="sm" onClick={() => setAmount(max)} className="flex-1">
              {t("allIn")}
            </Button>
          </div>
          <Button
            variant="primary"
            size="lg"
            block
            onClick={() => send({ t: "player:wager", amount: value })}
          >
            {t("lockInWager")}
          </Button>
        </div>
      </div>
    );
  }

  if (w.stage === "question") {
    return (
      <div className="flex-1 flex flex-col gap-4 p-4 overflow-y-auto">
        <div className="bg-board rounded-card p-4 text-center flex flex-col items-center gap-3">
          <QuestionImage src={w.image} className="max-h-[20vh] w-auto rounded-control object-contain" />
          <p className="font-semibold text-lg leading-snug">{w.question}</p>
        </div>
        {mine?.answered && (
          <p className="text-success text-center text-sm inline-flex items-center justify-center gap-1.5">
            <Icon name="check" /> {t("answerSubmitted")}
          </p>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (answer.trim()) send({ t: "player:answer", text: answer });
          }}
          className="flex flex-col gap-3"
        >
          <label className="type-eyebrow text-ink/40">{t("yourAnswer")}</label>
          <Input
            value={answer}
            onChange={(e) => setAnswer(e.target.value.slice(0, 120))}
            maxLength={120}
            className="text-lg py-3"
          />
          <Button type="submit" variant="primary" size="lg" block disabled={!answer.trim()}>
            {t("submitAnswer")}
          </Button>
        </form>
      </div>
    );
  }

  // reveal — the host is judging; everyone watches the big screen
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-5 p-6 text-center">
      <p className="type-display text-2xl text-accentLight">{w.question}</p>
      {mine && (
        <div
          className={`rounded-card p-5 w-full max-w-xs anim-pop ${
            mine.judged === "correct"
              ? "bg-successStrong text-white"
              : mine.judged === "wrong"
                ? "bg-dangerStrong text-white"
                : "bg-ink/[0.08]"
          }`}
        >
          <div className="type-display text-2xl break-words">{mine.answer || "—"}</div>
          <div className="text-sm opacity-75 mt-2 tabular">
            {t("wagerLabel")} {mine.wager}
          </div>
        </div>
      )}
      <p className="text-ink/35 text-sm">{t("waitingForHost")}</p>
    </div>
  );
}
